import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Box, makeStyles } from '@material-ui/core';
import Colors from '../../../helpers/Colors';
import { AppContext } from '../../../contexts/AppContext';
import assets from '../../../helpers/assets';
import DispatchButton from './DispatchButton';
import Text from '../Text';
import Steps from './Steps';
import useVisibilitySensor from '../../../helpers/useVisibilitySensor';
import { CubbyAssignButton } from "./CubbyAssginButton";
import DeliveryToggle from './DeliveryToggle';
import WarningMessage from "./WarningMessage";
import {isNoDeliveryPartner} from "../../../helpers/DeliveryPartnerHelper";
import {DASH, DELIVERY_FOOD_AGGREGATOR, DELIVERY_IN_HOUSE} from "../../../helpers/Constants";

function PickupRow({ order, delay }) {
    const classes = useStyles();
    const [visible, setVisible] = useState(false);

    const rootNode = useRef();
    const { isAssignerMode, scrollContainer, selectedTab, availableDeliveryModes } = useContext(AppContext);

    const { isVisible, visibilityRect } = useVisibilitySensor(rootNode, {
        intervalCheck: false,
        scrollCheck: true,
        resizeCheck: false,
        containment: scrollContainer.current,
        scrollDebounce: 100,
        screen: selectedTab,
    });

    /**
     * show DASH as a delivery mode when delivery mode is null or
     * delivery mode type is in_house with no delivery partners
     * @param selectedOrder
     * @returns {string|*}
     */
    const getDeliveryMode = (selectedOrder) => {
        if (!selectedOrder.deliveryMode ||
            (selectedOrder.deliveryMode?.deliveryModeType === DELIVERY_IN_HOUSE
                && isNoDeliveryPartner(order))) {
            return DASH;
        }
        return order.deliveryMode.deliveryModeDisplayName;
    }

    useEffect(() => {
        if (selectedTab !== 0) {
            return;
        }
        setVisible(isVisible);
    }, [isVisible])

    return useMemo(() => {
        return (
            <div ref={rootNode}>
                {/* <Animated
                    animationIn="flipInX"
                    animationOut="flipOutX"
                    isVisible={isVisible}
                    animationInDuration={250}
                    animationOutDuration={250}
                    animateOnMount={true}
                > */}
                <Box
                    className={order.overTime ? classes.contentLatedItemContainer : classes.contentItemContainer}
                >
                    <Box style={{display: 'flex', flex: '1'}}>
                        <Box className={classes.contentIdRowContainer}>
                            <Text className={classes.contentOrderIdLabel}>{order.externalId}</Text>
                        </Box>
                        <Box className={classes.contentRowContainer}>
                            {visible && <Text className={classes.contentLabel}>{order.brandInfo ? order.brandInfo.name : ''}</Text>}
                        </Box>
                        <Box className={classes.contentRowContainer}>
                            {visible && <Text className={classes.contentLabel}>{order.sourceInfo ? order.sourceInfo.name : ''}</Text>}
                        </Box>
                        <Box className={classes.contentRowContainer}>
                            {visible && <Text
                                className={classes.contentLabel}>{getDeliveryMode(
                                order)}</Text>}
                        </Box>
                        <Box className={classes.contentRowContainer}>
                            <Box className={classes.timerContainer}>
                                {visible && <Text className={order.overTime ? classes.contentLatedLabel : classes.contentLabel}>
                                    {order.elapsedTime > 0 ? order.elapsedTime : 0} MIN
                                </Text>}
                                {order.overTime && visible && (
                                    <Box className={classes.timerWarningIconContainer}>
                                        <img
                                            src={assets.warning.default}
                                            alt=""
                                            className={classes.warningIcon}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        <Box className={classes.contentRowStatusView}>
                            {visible && <Steps
                                type={'pickup'}
                                statusHistory={order.statusHistory}
                                order={order}
                            />}
                        </Box>
                        <Box className={classes.contentAdditionalContainer}>
                            <Box className={classes.contentButtonRowContainer}>
                                {isAssignerMode ?
                                    <CubbyAssignButton order={order} /> :
                                    <DispatchButton orderId={order.id} statusHistory={order.statusHistory} />}
                            </Box>
                            {
                                order.deliveryPartnerSelection && order.deliveryMode?.deliveryModeType !== DELIVERY_FOOD_AGGREGATOR ?
                                    <Box className={classes.contentButtonRowContainer} style={{ flex: 1 }}>
                                        <DeliveryToggle order={order} />
                                    </Box> :
                                    <Box className={classes.contentButtonRowContainer} style={{ flex: 1, minWidth: '160px' }}>
                                    </Box>
                            }
                        </Box>
                    </Box>
                    <Box style={{paddingLeft: 20}}>
                        <WarningMessage order={order} type={'pickup'}/>
                    </Box>
                </Box>
                {/* </Animated> */}
            </div>
        );
    }, [order, order.elapsedTime, JSON.stringify(order.statusHistory), isVisible, visible]);
}

export default PickupRow;


const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        width: '100%',
        flex: 1,
        height: '100%',
    },
    contentOrderIdLabel: {
        color: Colors.textColor,
        opacity: 0.6,
        fontSize: 16,
        fontWeight: '600',
        paddingLeft: 20,
        fontFamily: 'DINNextLTPro-Regular',
    },
    contentLabel: {
        color: Colors.textColor,
        opacity: 0.6,
        fontSize: 16,
        fontWeight: '600',
        fontFamily: 'DINNextLTPro-Regular',
    },
    contentLatedLabel: {
        color: Colors.red,
        opacity: 0.6,
        fontSize: 16,
        fontWeight: '600',
        fontFamily: 'DINNextLTPro-Regular',
    },
    contentItemContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: 4,
        border: '1px solid #EDEDED',
        marginBottom: 8,
    },
    contentItemContainerBalnk: {
        height: 60,
        marginBottom: 8,
    },
    contentLatedItemContainer: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        height: '100%',
        minHeight: '60px',
        border: '1px solid #EDEDED',
        marginBottom: 8,
        borderRadius: 4,
        backgroundColor: Colors.fadedRed,
    },
    contentIdRowContainer: {
        display: 'flex',
        flex: 1.8,
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
    contentRowContainer: {
        display: 'flex',
        flex: 1,
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
    contentAdditionalContainer: {
        display: 'flex',
        flex: 3,
        justifyContent: 'flex-end',
        paddingRight: 15,
        alignItems: 'center',
    },
    contentButtonRowContainer: {
        display: 'flex',
        flex: 1,
        justifyContent: 'flex-end',
        paddingRight: 15,
        alignItems: 'center',
    },
    contentRowStatusView: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flex: 6.5,
    },
    warningIcon: {
        height: 10,
        width: 10,
        objectFit: 'contain',
    },
    timerContainer: {
        display: 'flex',
        flexDirection: 'row',
    },
    timerWarningIconContainer: {
        height: 18,
        width: 18,
        borderRadius: 20,
        marginLeft: 6,
        backgroundColor: Colors.red,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
}));
